import React from 'react';
import { Calendar, Download, Mail, Newspaper, FileText } from 'lucide-react';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
  MarketingPrimaryButton,
} from '../../components/home/MarketingPageChrome';
import { pageValue, sectionOrder } from '../../content/publicPages';
import { SectionLayoutProvider, SectionSlot } from '../../content/sectionTone';
import { usePublicPage } from '../../hooks/usePublicPage';

const PRESS_HERO =
  'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1800&q=80';

const Press = () => {
  const { saved, defaults } = usePublicPage('press');
  const val = (key) => pageValue(saved, {}, defaults, key);
  const order = sectionOrder(saved, 'press');
  const kitIcons = [Download, FileText, Newspaper, Download];
  const pressReleases = val('releases') || [];
  const mediaKit = (val('kit') || []).map((item, index) => ({
    icon: kitIcons[index % kitIcons.length],
    title: item.title,
    description: item.description,
  }));
  return (
    <SectionLayoutProvider order={order} colors={saved.section_colors} hidden={saved.hidden_sections}>
    <MarketingPage>
      <SectionSlot id="hero">
      <MarketingMediaHero
        title={val('title')}
        subtitle={val('subtitle')}
        image={val('image') || PRESS_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#press-releases">{val('hero_primary_label')}</MarketingHeroLightButton>
            <MarketingGhostOnDark to={val('hero_secondary_link') || '/contact?subject=Press%20inquiry'}>
              {val('hero_secondary_label')}
            </MarketingGhostOnDark>
          </>
        }
      />
      </SectionSlot>

      <SectionSlot id="intro">
      <MarketingSection tone="white">
        <MarketingSplitIntro title={val('intro_title')}>
          <p>{val('intro_body')}</p>
        </MarketingSplitIntro>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="releases">
      <MarketingSection tone="white" id="press-releases" className="!pt-0 sm:!pt-0">
        <div className="mx-auto max-w-3xl">
          <MarketingReveal className="mb-8">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              {val('releases_title')}
            </h2>
          </MarketingReveal>
          <div className="divide-y divide-m-border-light border-y border-m-border-light">
            {pressReleases.map((release) => (
              <article key={release.title} className="py-8">
                <div
                  className="mb-3 flex flex-wrap items-center gap-3 text-xs"
                  style={{ color: '#8a8a8a' }}
                >
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {release.date}
                  </span>
                  <span>{release.category}</span>
                </div>
                <h3 className="mb-2 text-lg font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
                  {release.title}
                </h3>
                <p className="text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
                  {release.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="kit">
      <MarketingSection tone="canvas">
        <div className="mx-auto max-w-6xl">
          <MarketingReveal className="mb-8 max-w-xl">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              {val('kit_title')}
            </h2>
          </MarketingReveal>
          <MarketingFeatureGrid items={mediaKit} />
        </div>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="closing">
      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <Mail className="mx-auto mb-5 h-6 w-6 text-white/70" />
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            {val('closing_title')}
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            {val('closing_body')}
          </p>
          <MarketingPrimaryButton
            to={val('closing_link') || '/contact?subject=Press%20inquiry'}
            className="!bg-white !text-m-ink hover:!bg-white/90"
          >
            {val('closing_button')}
          </MarketingPrimaryButton>
        </MarketingReveal>
      </MarketingSection>
      </SectionSlot>
    </MarketingPage>
    </SectionLayoutProvider>
  );
};

export default Press;
